/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics';
import type { GetEditorContainerWidth, GetEditorFeatureFlags } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	akEditorFloatingDialogZIndex,
	akEditorFloatingOverlapPanelZIndex,
} from '@atlaskit/editor-shared-styles';
import {
	findCellRectClosestToPos,
	getSelectionRect,
	isSelectionType,
} from '@atlaskit/editor-tables/utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import { getPluginState } from '../../pm-plugins/plugin-factory';
import type { PluginConfig, PluginInjectionAPI } from '../../types';
import {
	contextualMenuDropdownWidth,
	contextualMenuDropdownWidthDnD,
	contextualMenuTriggerSize,
	tablePopupMenuFitHeight,
} from '../consts';

// Ignored via go/ees005
// eslint-disable-next-line import/no-named-as-default
import ContextualMenu from './ContextualMenu';
import { tablePopupStyles } from './styles';

interface Props {
	api: PluginInjectionAPI | undefined | null;
	boundariesElement?: HTMLElement;
	editorAnalyticsAPI?: EditorAnalyticsAPI;
	editorView: EditorView | undefined;
	getEditorContainerWidth: GetEditorContainerWidth;
	getEditorFeatureFlags?: GetEditorFeatureFlags;
	isCellMenuOpenByKeyboard?: boolean;
	isCommentEditor?: boolean;
	isDragMenuOpen?: boolean;
	isOpen: boolean;
	mountPoint?: HTMLElement;
	pluginConfig?: PluginConfig;
	scrollableElement?: HTMLElement;
	targetCellPosition?: number;
}

const FloatingContextualMenu = ({
	mountPoint,
	boundariesElement,
	scrollableElement,
	editorView,
	isOpen,
	pluginConfig,
	editorAnalyticsAPI,
	getEditorContainerWidth,
	getEditorFeatureFlags,
	isCellMenuOpenByKeyboard,
	isCommentEditor,
	isDragMenuOpen,
	api,
}: Props) => {
	if (expValEquals('platform_editor_hydratable_ui', 'isEnabled', true) && !editorView) {
		return null;
	}

	// TargetCellPosition could be outdated: https://product-fabric.atlassian.net/browse/ED-8129
	// Remove ! during platform_editor_hydratable_ui cleanup
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const { targetCellPosition, isDragAndDropEnabled } = getPluginState(editorView!.state);
	// Remove ! during platform_editor_hydratable_ui cleanup
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	if (!isOpen || !targetCellPosition || editorView!.state.doc.nodeSize <= targetCellPosition) {
		return null;
	}

	// Remove ! during platform_editor_hydratable_ui cleanup
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const { selection } = editorView!.state;
	const selectionRect = isSelectionType(selection, 'cell')
		? // Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			getSelectionRect(selection)!
		: findCellRectClosestToPos(selection.$from);

	if (!selectionRect) {
		return null;
	}
	// Remove ! during platform_editor_hydratable_ui cleanup
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const domAtPos = editorView!.domAtPos.bind(editorView);
	const targetCellRef = findDomRefAtPos(targetCellPosition, domAtPos);
	if (!targetCellRef) {
		return null;
	}

	const parentSticky =
		targetCellRef.parentElement && targetCellRef.parentElement.className.indexOf('sticky') > -1;

	return (
		<Popup
			alignX="right"
			alignY="top"
			// Ignored via go/ees005
			// eslint-disable-next-line @atlaskit/editor/no-as-casting
			target={targetCellRef as HTMLElement}
			mountTo={mountPoint}
			boundariesElement={boundariesElement}
			scrollableElement={scrollableElement}
			fitHeight={tablePopupMenuFitHeight}
			fitWidth={isDragAndDropEnabled ? contextualMenuDropdownWidthDnD : contextualMenuDropdownWidth}
			// z-index value below is to ensure that this menu is above other floating menu
			// in table, but below floating dialogs like typeaheads, pickers, etc.
			zIndex={parentSticky ? akEditorFloatingDialogZIndex : akEditorFloatingOverlapPanelZIndex}
			forcePlacement={true}
			offset={[-7, 0]}
			stick={true}
		>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
			<div css={tablePopupStyles(isDragAndDropEnabled)}>
				<ContextualMenu
					// Remove ! during platform_editor_hydratable_ui cleanup
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					editorView={editorView!}
					offset={[contextualMenuTriggerSize / 2, -contextualMenuTriggerSize]}
					isOpen={isOpen}
					targetCellPosition={targetCellPosition}
					allowColumnSorting={pluginConfig && pluginConfig.allowColumnSorting}
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					allowMergeCells={pluginConfig!.allowMergeCells}
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					allowBackgroundColor={pluginConfig!.allowBackgroundColor}
					selectionRect={selectionRect}
					mountPoint={fg('platform_editor_fix_table_menus_jira') ? mountPoint : undefined}
					boundariesElement={boundariesElement}
					editorAnalyticsAPI={editorAnalyticsAPI}
					getEditorContainerWidth={getEditorContainerWidth}
					getEditorFeatureFlags={getEditorFeatureFlags}
					isCellMenuOpenByKeyboard={isCellMenuOpenByKeyboard}
					isCommentEditor={isCommentEditor}
					api={api}
					isDragMenuOpen={
						expValNoExposure('platform_editor_lovability_user_intent', 'isEnabled', true)
							? isDragMenuOpen
							: undefined
					}
				/>
			</div>
		</Popup>
	);
};

FloatingContextualMenu.displayName = 'FloatingContextualMenu';

export default FloatingContextualMenu;
